import type { AreaFrameUpdateData } from "@situate/contracts";
import type { Aoi } from "@/features/deployment-dashboard/live-view/model/aoi";
import { calculatePolygonArea } from "@/features/deployment-dashboard/live-view/utils/polygon-geometry";

/** Maps AOI id to how many currently tracked people fall inside its polygon. */
export type AoiPopulations = Record<string, number>;

/** Maps AOI id to its current people-per-square-meter density, or null if its area is zero. */
export type AoiDensities = Record<string, number | null>;

interface CompiledPolygonEdge {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  deltaX: number;
  deltaY: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  boundaryTolerance: number;
  raySlope: number;
}

interface CompiledAoiPolygon {
  id: string;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  edges: CompiledPolygonEdge[];
}

export interface CompiledAoiPopulationIndex {
  readonly aoiIds: readonly string[];
  readonly polygons: readonly CompiledAoiPolygon[];
  readonly cells: ReadonlyMap<number, readonly number[]>;
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
  readonly cellWidth: number;
  readonly cellHeight: number;
  readonly gridDimension: number;
}

const MIN_GRID_DIMENSION = 4;
const MAX_GRID_DIMENSION = 32;
const TARGET_CELLS_PER_AOI = 4;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getGridDimension(polygonCount: number): number {
  if (polygonCount === 0) return 0;
  return clamp(
    Math.ceil(Math.sqrt(polygonCount * TARGET_CELLS_PER_AOI)),
    MIN_GRID_DIMENSION,
    MAX_GRID_DIMENSION
  );
}

function toCellCoordinate(
  value: number,
  minimum: number,
  cellSize: number,
  gridDimension: number
): number {
  return clamp(Math.floor((value - minimum) / cellSize), 0, gridDimension - 1);
}

function compilePolygon(aoi: Aoi): CompiledAoiPolygon | null {
  if (aoi.points.length < 3) return null;

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  const edges: CompiledPolygonEdge[] = [];

  for (
    let index = 0, previousIndex = aoi.points.length - 1;
    index < aoi.points.length;
    index += 1
  ) {
    const start = aoi.points.at(previousIndex)!;
    const end = aoi.points.at(index)!;
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;

    minX = Math.min(minX, end.x);
    maxX = Math.max(maxX, end.x);
    minY = Math.min(minY, end.y);
    maxY = Math.max(maxY, end.y);
    edges.push({
      startX: start.x,
      startY: start.y,
      endX: end.x,
      endY: end.y,
      deltaX,
      deltaY,
      minX: Math.min(start.x, end.x),
      maxX: Math.max(start.x, end.x),
      minY: Math.min(start.y, end.y),
      maxY: Math.max(start.y, end.y),
      boundaryTolerance: 1e-7 * Math.max(1, Math.hypot(deltaX, deltaY)),
      raySlope: deltaY === 0 ? 0 : (start.x - end.x) / (start.y - end.y),
    });
    previousIndex = index;
  }

  return { id: aoi.id, minX, maxX, minY, maxY, edges };
}

/**
 * Compiles static AOI geometry into reusable edges, bounds, and a uniform-grid
 * spatial index. Call this only when AOIs change, not for every live frame.
 */
export function compileAoiPopulationIndex(aois: Aoi[]): CompiledAoiPopulationIndex {
  const polygons = aois.flatMap((aoi) => {
    const polygon = compilePolygon(aoi);
    return polygon ? [polygon] : [];
  });
  const gridDimension = getGridDimension(polygons.length);

  if (polygons.length === 0) {
    return {
      aoiIds: aois.map((aoi) => aoi.id),
      polygons,
      cells: new Map(),
      minX: 0,
      maxX: 0,
      minY: 0,
      maxY: 0,
      cellWidth: 1,
      cellHeight: 1,
      gridDimension,
    };
  }

  const minX = Math.min(...polygons.map((polygon) => polygon.minX));
  const maxX = Math.max(...polygons.map((polygon) => polygon.maxX));
  const minY = Math.min(...polygons.map((polygon) => polygon.minY));
  const maxY = Math.max(...polygons.map((polygon) => polygon.maxY));
  const cellWidth = Math.max((maxX - minX) / gridDimension, Number.EPSILON);
  const cellHeight = Math.max((maxY - minY) / gridDimension, Number.EPSILON);
  const cells = new Map<number, number[]>();

  polygons.forEach((polygon, polygonIndex) => {
    const firstCellX = toCellCoordinate(polygon.minX, minX, cellWidth, gridDimension);
    const lastCellX = toCellCoordinate(polygon.maxX, minX, cellWidth, gridDimension);
    const firstCellY = toCellCoordinate(polygon.minY, minY, cellHeight, gridDimension);
    const lastCellY = toCellCoordinate(polygon.maxY, minY, cellHeight, gridDimension);

    for (let cellY = firstCellY; cellY <= lastCellY; cellY += 1) {
      for (let cellX = firstCellX; cellX <= lastCellX; cellX += 1) {
        const cellKey = cellY * gridDimension + cellX;
        const candidates = cells.get(cellKey);
        if (candidates) {
          candidates.push(polygonIndex);
        } else {
          cells.set(cellKey, [polygonIndex]);
        }
      }
    }
  });

  return {
    aoiIds: aois.map((aoi) => aoi.id),
    polygons,
    cells,
    minX,
    maxX,
    minY,
    maxY,
    cellWidth,
    cellHeight,
    gridDimension,
  };
}

function isPointInsideCompiledPolygon(x: number, y: number, polygon: CompiledAoiPolygon): boolean {
  if (x < polygon.minX || x > polygon.maxX || y < polygon.minY || y > polygon.maxY) {
    return false;
  }

  let inside = false;
  for (const edge of polygon.edges) {
    const pointX = x - edge.startX;
    const pointY = y - edge.startY;
    const crossProduct = edge.deltaX * pointY - edge.deltaY * pointX;
    if (
      Math.abs(crossProduct) <= edge.boundaryTolerance &&
      x >= edge.minX &&
      x <= edge.maxX &&
      y >= edge.minY &&
      y <= edge.maxY
    ) {
      return true;
    }

    const crossesHorizontalRay =
      edge.endY > y !== edge.startY > y && x < edge.raySlope * (y - edge.endY) + edge.endX;
    if (crossesHorizontalRay) inside = !inside;
  }

  return inside;
}

/**
 * Counts every currently subscribed live object whose coordinates fall inside
 * each AOI polygon. Each person queries one spatial-index cell and runs the
 * exact polygon test only against AOIs whose bounding boxes cover that cell.
 */
export function deriveAoiPopulations(
  index: CompiledAoiPopulationIndex,
  latestAreaFrames: Record<string, AreaFrameUpdateData>
): AoiPopulations {
  const populations: AoiPopulations = Object.fromEntries(index.aoiIds.map((id) => [id, 0]));
  if (index.polygons.length === 0) return populations;

  for (const frame of Object.values(latestAreaFrames)) {
    for (const object of frame.objects) {
      const { x, y } = object.position;
      if (x < index.minX || x > index.maxX || y < index.minY || y > index.maxY) continue;

      const cellX = toCellCoordinate(x, index.minX, index.cellWidth, index.gridDimension);
      const cellY = toCellCoordinate(y, index.minY, index.cellHeight, index.gridDimension);
      const candidates = index.cells.get(cellY * index.gridDimension + cellX);
      if (!candidates) continue;

      for (const polygonIndex of candidates) {
        const polygon = index.polygons.at(polygonIndex)!;
        if (isPointInsideCompiledPolygon(x, y, polygon)) {
          populations[polygon.id] = (populations[polygon.id] ?? 0) + 1;
        }
      }
    }
  }

  return populations;
}

/** Derives each AOI's people-per-square-meter density from its current population and polygon area. */
export function deriveAoiDensities(aois: Aoi[], populations: AoiPopulations): AoiDensities {
  return Object.fromEntries(
    aois.map((aoi) => {
      const areaSquareMeters = calculatePolygonArea(aoi.points);
      const population = populations[aoi.id] ?? 0;
      return [aoi.id, areaSquareMeters > 0 ? population / areaSquareMeters : null];
    })
  );
}
